import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, LogOut, Users, ShieldCheck, Briefcase, Building2, Clock, FileLock, History } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { portalApi, portalAuth } from '@/lib/portalApi';
import { useFocusItem } from '@/hooks/useFocusItem';
import {
  SectionShell,
  ActionItemsSection,
  DocumentRequestsSection,
  SharedDocumentsSection,
  WhatsChangedSection,
  MessagesSection,
} from '@/components/portal/FamilyPortalSections';

function PortalShell({ children, title, subtitle, showBack }) {
  const navigate = useNavigate();
  const user = portalAuth.getUser();

  const logout = () => {
    portalAuth.clear();
    navigate('/portal/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F9F7F5]" data-testid="adviser-portal-shell">
      <header className="bg-white border-b border-[#E6E0D9] sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {showBack ? (
              <Button variant="ghost" size="sm" onClick={() => navigate('/portal/adviser')} className="gap-1 -ml-2" data-testid="portal-back-btn">
                <ArrowLeft className="w-4 h-4" /> Back
              </Button>
            ) : (
              <a
                href="/"
                className="inline-flex items-center gap-1 text-xs text-[#8C8078] hover:text-[#A86A3A] -ml-2 px-2 py-1 transition-colors"
                data-testid="portal-back-to-website-btn"
                title="Back to QIndex One website"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> QIndex One
              </a>
            )}
            <div className="min-w-0">
              <h1 className="font-heading text-base sm:text-lg text-[#2C2420] truncate">{title}</h1>
              {subtitle && <p className="text-[10px] sm:text-xs text-[#8C8078] truncate">{subtitle}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex flex-col items-end">
              <span className="text-xs text-[#2C2420]">{user?.name}</span>
              <Badge className="bg-[#F0EBE6] text-[#4A3B32] text-[9px] mt-0.5">External Adviser</Badge>
            </div>
            <Button variant="ghost" size="icon" onClick={logout} title="Sign out" data-testid="portal-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">{children}</main>
    </div>
  );
}

export function AdviserPortalDashboard() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!portalAuth.getToken()) {
      navigate('/portal/login', { replace: true });
      return;
    }
    portalApi.listClients()
      .then(res => setClients(res.data.clients || []))
      .catch(() => navigate('/portal/login', { replace: true }))
      .finally(() => setLoading(false));
  }, [navigate]);

  const user = portalAuth.getUser();

  return (
    <PortalShell title="My Clients" subtitle={`${clients.length} client${clients.length === 1 ? '' : 's'} you have access to`}>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#4A3B32]" /></div>
      ) : clients.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#E6E0D9] bg-white p-12 text-center" data-testid="adviser-empty-state">
          <Users className="w-10 h-10 text-[#8C8078] mx-auto mb-3" />
          <h3 className="font-medium text-[#2C2420] mb-1">No clients yet</h3>
          <p className="text-sm text-[#8C8078]">
            You'll see clients here once your contact at {user?.organisationName || 'the firm'} grants you access.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {clients.map(c => (
            <Link
              key={c.id}
              to={`/portal/adviser/clients/${c.id}`}
              className="rounded-xl border border-[#E6E0D9] bg-white p-4 hover:border-[#A86A3A]/40 hover:shadow-sm transition-all"
              data-testid={`adviser-portal-client-${c.id}`}
            >
              <div className="flex items-start justify-between mb-2">
                <Briefcase className="w-5 h-5 text-[#A86A3A]" />
                {c.segment && <Badge className="bg-[#F0EBE6] text-[#4A3B32] text-[9px]">{c.segment}</Badge>}
              </div>
              <h3 className="font-medium text-[#2C2420] truncate">{c.name}</h3>
              {c.netWorth ? (
                <p className="text-[10px] text-[#8C8078] mt-1">Net Worth: ${(c.netWorth / 1000000).toFixed(1)}m</p>
              ) : null}
            </Link>
          ))}
        </div>
      )}
    </PortalShell>
  );
}

export function AdviserPortalClient() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [client, setClient] = useState(null);
  const [context, setContext] = useState(null);
  const [actionItems, setActionItems] = useState([]);
  const [docRequests, setDocRequests] = useState([]);
  const [sharedDocs, setSharedDocs] = useState([]);
  const [teamUpdates, setTeamUpdates] = useState([]);
  const [comments, setComments] = useState([]);
  const [auditSummary, setAuditSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  useFocusItem(!loading);

  const loadAll = useCallback(async (vis) => {
    const calls = [
      portalApi.clientContext(clientId).then((r) => setContext(r.data)).catch(() => {}),
      vis.action_items && portalApi.actionItems(clientId).then((r) => setActionItems(r.data?.items || [])).catch(() => {}),
      vis.document_requests && portalApi.documentRequests(clientId).then((r) => setDocRequests(r.data?.items || [])).catch(() => {}),
      vis.documents && portalApi.sharedDocuments(clientId).then((r) => setSharedDocs(r.data?.items || [])).catch(() => {}),
      vis.team_updates && portalApi.whatsChanged(clientId).then((r) => setTeamUpdates(r.data?.items || [])).catch(() => {}),
      vis.comments && portalApi.messages(clientId).then((r) => setComments(r.data?.items || [])).catch(() => {}),
      portalApi.myAuditSummary().then((r) => setAuditSummary(r.data)).catch(() => {}),
    ].filter(Boolean);
    await Promise.all(calls);
  }, [clientId]);

  useEffect(() => {
    if (!portalAuth.getToken()) {
      navigate('/portal/login', { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const meRes = await portalApi.me();
        if (cancelled) return;
        setMe(meRes.data);
        // Light client header info via the existing endpoint
        const det = await portalApi.getClient(clientId);
        if (cancelled) return;
        setClient(det.data?.client);
        await loadAll(meRes.data?.visibility || {});
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load client');
        navigate('/portal/adviser');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, navigate, loadAll]);

  const postComment = async (text) => {
    setPosting(true);
    try {
      const res = await portalApi.postMessage(clientId, text);
      setComments((prev) => [res.data?.entry, ...prev]);
    } catch (err) {
      toast.error('Could not post comment');
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return (
      <PortalShell title="Loading…" showBack>
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#4A3B32]" /></div>
      </PortalShell>
    );
  }
  if (!client) return null;

  const vis = me?.visibility || {};
  const matterScope = (me?.matterScope || '').trim();

  return (
    <PortalShell title={client.name} subtitle={client.segment} showBack>
      <div className="space-y-5">
        {/* 1. Welcome / matter scope */}
        <SectionShell title="Matter scope" subtitle="The remit your contact at the firm has granted you" icon={Briefcase} testId="adviser-matter-scope-section">
          {matterScope ? (
            <p className="text-sm text-[#2C2420] leading-relaxed whitespace-pre-wrap" data-testid="adviser-matter-scope-text">{matterScope}</p>
          ) : (
            <p className="text-sm text-[#8C8078]">No matter scope set. Reach out to your contact at the firm for clarification.</p>
          )}
        </SectionShell>

        {/* 2. Items needing your input */}
        {vis.action_items !== false && <ActionItemsSection items={actionItems} />}

        {/* 3. Document requests */}
        {vis.document_requests !== false && <DocumentRequestsSection items={docRequests} />}

        {/* 4. Documents shared with you */}
        {vis.documents !== false && <SharedDocumentsSection items={sharedDocs} />}

        {/* 5. Relevant client / entity context */}
        {(vis.client_context !== false) && (
          <SectionShell title="Client context" subtitle="Read-only summary of the family and entities" icon={Building2} testId="adviser-client-context-section">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#8C8078] mb-2">Family members</p>
                {(context?.individuals || []).length === 0 ? (
                  <p className="text-sm text-[#8C8078]">None on file.</p>
                ) : (
                  <ul className="space-y-1.5" data-testid="adviser-context-individuals">
                    {(context?.individuals || []).map((i) => (
                      <li key={i.id} className="text-sm text-[#2C2420] flex items-baseline justify-between gap-2">
                        <span className="truncate">{i.firstName} {i.lastName}</span>
                        <span className="text-xs text-[#8C8078]">{i.familyRole || i.role || ''}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#8C8078] mb-2">Entities</p>
                {(context?.entities || []).length === 0 ? (
                  <p className="text-sm text-[#8C8078]">None on file.</p>
                ) : (
                  <ul className="space-y-1.5" data-testid="adviser-context-entities">
                    {(context?.entities || []).map((e) => (
                      <li key={e.id} className="text-sm text-[#2C2420] flex items-baseline justify-between gap-2">
                        <span className="truncate">{e.name}</span>
                        <Badge className="bg-[#F0EBE6] text-[#4A3B32] text-[9px]">{e.type}</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </SectionShell>
        )}

        {/* 6. Latest from the team */}
        {vis.team_updates !== false && <WhatsChangedSection items={teamUpdates} />}

        {/* 7. Comment / response form */}
        {vis.comments !== false && (
          <MessagesSection
            items={comments}
            canPost={(me?.permissions || {}).canComment !== false}
            onPost={postComment}
            posting={posting}
            currentUserId={me?.id}
          />
        )}

        {/* 8. Access expiry & audit note */}
        <AccessAuditCard summary={auditSummary} />
      </div>
    </PortalShell>
  );
}

function AccessAuditCard({ summary }) {
  if (!summary) return null;
  const { expiresAt, lastLoginAt, totalLoggedEvents, recentOwnEvents = [] } = summary;
  return (
    <section className="rounded-2xl border border-[#E6D9C4] bg-[#FFF8F0] p-5 sm:p-6" data-testid="adviser-audit-section">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg bg-[#A86A3A]/15 flex items-center justify-center shrink-0">
          <ShieldCheck className="w-4 h-4 text-[#A86A3A]" />
        </div>
        <div>
          <h2 className="font-heading text-lg text-[#2C2420] leading-tight">Access & audit</h2>
          <p className="text-sm text-[#5D524D] mt-0.5">Transparency about what's logged when you sign in.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-4">
        <KpiTile icon={Clock} label="Access expires" value={expiresAt ? format(parseISO(expiresAt), 'd MMM yyyy') : 'No expiry'} />
        <KpiTile icon={FileLock} label="Last sign-in" value={lastLoginAt ? format(parseISO(lastLoginAt), "d MMM 'at' h:mm a") : '—'} />
        <KpiTile icon={History} label="Events logged" value={totalLoggedEvents?.toLocaleString() ?? '0'} />
      </div>

      {recentOwnEvents.length > 0 && (
        <div className="pt-3 border-t border-[#E6D9C4]">
          <p className="text-[10px] uppercase tracking-wider text-[#A86A3A] mb-2">Your recent actions</p>
          <ul className="space-y-1.5" data-testid="adviser-audit-recent">
            {recentOwnEvents.map((e, idx) => (
              <li key={idx} className="text-xs text-[#5D524D] flex items-center justify-between gap-2">
                <span>
                  <span className="text-[#2C2420] font-medium">{e.action?.replace('_', ' ')}</span>
                  {e.object && <span className="text-[#8C8078]"> · {e.object}</span>}
                </span>
                <span className="text-[#8C8078] shrink-0">{e.timestamp && format(parseISO(e.timestamp), "d MMM, h:mm a")}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function KpiTile({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg bg-white border border-[#E6D9C4] p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#8C8078]">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <p className="text-sm text-[#2C2420] mt-1 tabular-nums">{value}</p>
    </div>
  );
}
